import { useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import MfaEnrollmentScreen from './MfaEnrollmentScreen.jsx';
import { useAuth } from '../shared/context/AuthContext';

export default function MfaEnrollmentGuard() {
  const { user, claims, mfaEnabled, isAuthResolved } = useAuth();
  const [enrollmentUser, setEnrollmentUser] = useState(null);
  const [isChecking, setIsChecking] = useState(true);

  const checkEnrollment = useCallback(async (currentUser, currentClaims, isMfaRequired) => {
    if (!currentUser) {
      setEnrollmentUser(null);
      setIsChecking(false);
      return;
    }
    try {
      // enrolledFactors may not be populated immediately after MFA challenge sign-in,
      // so also check the token's sign_in_second_factor as a reliable fallback.
      const isEnrolledFromToken = currentClaims?.firebase?.sign_in_second_factor != null;
      // Custom tokens are only issued by signInWithTrustedDevice after verifying
      // both password and trusted device cookie - treat as satisfying MFA.
      const isTrustedDeviceSignIn = currentClaims?.firebase?.sign_in_provider === 'custom';

      // reload() refreshes the user's enrolledFactors profile data only - it never
      // touches claims/token, so it does not conflict with AuthContext's non-force
      // getIdTokenResult() read (see AuthContext.jsx for why that stays non-force).
      if (isMfaRequired && !isEnrolledFromToken && !isTrustedDeviceSignIn) {
        await currentUser.reload();
      }

      const isEnrolledFromUser = (currentUser.multiFactor?.enrolledFactors?.length ?? 0) > 0;
      const isEnrolled = isEnrolledFromUser || isEnrolledFromToken || isTrustedDeviceSignIn;
      setEnrollmentUser(isMfaRequired && !isEnrolled ? currentUser : null);
    } catch {
      setEnrollmentUser(null);
    } finally {
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthResolved) return;
    setIsChecking(true);
    checkEnrollment(user, claims, mfaEnabled);
  }, [isAuthResolved, user, claims, mfaEnabled, checkEnrollment]);

  if (!isAuthResolved || isChecking) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (enrollmentUser) {
    return (
      <MfaEnrollmentScreen
        user={enrollmentUser}
        onEnrolled={() => { setEnrollmentUser(null); setIsChecking(false); }}
      />
    );
  }

  return <Outlet />;
}
